import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useDealerNotifications } from '../hooks/useDealerNotifications';
import { Bell, X, CheckCircle, AlertCircle, Info, Play } from 'lucide-react';
import notificationSound from '../utils/notificationSound';

const NotificationCenter = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 380px;
  max-height: 360px;
  background: #1B1C30;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  overflow: hidden;
    @media (max-width: 575px) {
    width: 95%;
    right: 5%;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
`;

const HeaderTitle = styled.h3`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  color: ${props => props.connected ? '#4ECDC4' : '#F43F60'};
  font-weight: 500;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.connected ? '#4ECDC4' : '#F43F60'};
`;

const NotificationCount = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background: #F43F60;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: 'Clash Grotesk', sans-serif;
`;

const NotificationsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const NotificationItem = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const NotificationIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => {
    switch (props.priority) {
      case 'high': return '#F43F60';
      case 'medium': return '#FFA726';
      case 'low': return '#4ECDC4';
      default: return '#9E9E9E';
    }
  }};
`;

const PriorityBadge = styled.span`
  font-size: 0.625rem;
  text-transform: uppercase;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => {
    switch (props.priority) {
      case 'high': return 'rgba(244, 63, 96, 0.1)';
      case 'medium': return 'rgba(255, 167, 38, 0.1)';
      case 'low': return 'rgba(78, 205, 196, 0.1)';
      default: return 'rgba(158, 158, 158, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.priority) {
      case 'high': return '#F43F60';
      case 'medium': return '#FFA726';
      case 'low': return '#4ECDC4';
      default: return '#9E9E9E';
    }
  }};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #9E9E9E;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ededed;
  }
`;

const NotificationTitle = styled.div`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 4px;
`;

const NotificationMessage = styled.div`
  color: #B4B4B4;
  font-size: 0.8125rem;
  line-height: 1.4;
  margin-bottom: 8px;
`;

const NotificationTime = styled.div`
  color: #9E9E9E;
  font-size: 0.75rem;
`;

const ClearAllButton = styled.button`
  width: 100%;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #B4B4B4;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ededed;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #9E9E9E;
  font-size: 16px;
`;

const DealerNotificationCenter = ({ token }) => {
  const { notifications, isConnected, clearNotifications, removeNotification, showNotificationToast } = useDealerNotifications(token);
  const [isOpen, setIsOpen] = useState(false);



  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_offer': return <CheckCircle size={16} />;
      case 'vehicle_update': return <Info size={16} />;
      case 'new_schedule': return <Info size={16} />;
      case 'new_message': return <Info size={16} />;
      case 'system_notification': return <AlertCircle size={16} />;
      default: return <Info size={16} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#F43F60';
      case 'medium': return '#FFA726';
      case 'low': return '#4ECDC4';
      default: return '#9E9E9E';
    }
  };

  if (!isOpen) {
    return (
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <button
          onClick={() => setIsOpen(true)}
          style={{
            background: '#222336',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#ededed',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#222336';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#222336';
          }}
        >
          <Bell size={20} />
          {notifications.length > 0 && (
            <NotificationCount>{notifications.length}</NotificationCount>
          )}
        </button>
      </div>
    );
  }

  return (
    <NotificationCenter>
      <Header>
        <HeaderTitle>
          <Bell size={18} />
          Notifications
          <ConnectionStatus connected={isConnected}>
            <StatusDot connected={isConnected} />
            {isConnected ? 'Connected' : 'Disconnected'}
          </ConnectionStatus>
        </HeaderTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        
          <CloseButton onClick={() => setIsOpen(false)}>
            <X size={16} />
          </CloseButton>
        </div>
      </Header>

      <NotificationsList>
        {notifications.length === 0 ? (
          <EmptyState>
            No notifications yet
          </EmptyState>
        ) : (
          notifications.map((notification, index) => (
            <NotificationItem key={index}>
              <NotificationHeader>
                <NotificationIcon priority={notification.priority}>
                  {getNotificationIcon(notification.type)}
                  <PriorityBadge priority={notification.priority}>
                    {notification.priority}
                  </PriorityBadge>
                </NotificationIcon>
                <CloseButton onClick={() => removeNotification(index)}>
                  <X size={14} />
                </CloseButton>
              </NotificationHeader>
              <NotificationTitle>{notification.title}</NotificationTitle>
              <NotificationMessage>{notification.message}</NotificationMessage>
              <NotificationTime>
                {new Date(notification.timestamp).toLocaleString()}
              </NotificationTime>
            </NotificationItem>
          ))
        )}
      </NotificationsList>

      {notifications.length > 0 && (
        <ClearAllButton onClick={clearNotifications}>
          Clear All Notifications
        </ClearAllButton>
      )}
    </NotificationCenter>
  );
};

export default DealerNotificationCenter; 